import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, ChevronDown } from "lucide-react";
import { config, getProduct, reviews, ingredients, fraiseIngredient, KIT_OFFERS, VARIANT_IMAGES, GALLERIES } from "../config";
import { Stars, Badge, Reveal, ReviewCard } from "../components/Primitives";
import { ProductCard } from "../components/ProductCard";
import { OfferSelector } from "../components/OfferSelector";
import { Gallery } from "../components/Gallery";
import { useCart } from "../context/CartContext";

const CONTENT = {
  kit: {
    h1: "LE KIT DENTAIRE COMPLET.",
    bullets: ["Des dents lisses comme après un détartrage, dès le premier soir", "Un émail nourri chaque jour avec le minéral dont il est fait", "Fini le tube au fond du placard : un objet beau, rechargeable à vie"],
    desc: "Le kit réunit les 4 essentiels : pastilles dentifrice à la nano-hydroxyapatite, pastilles bain de bouche, brosse en bambou à tête interchangeable et boîte de transport. Une seule décision, et votre routine entière change : plus de tube au milieu du lavabo, plus de dose au hasard, plus de liste d'ingrédients illisible. Un geste précis, une salle de bain épurée, et un émail qui reçoit chaque jour le minéral dont il est fait.",
    science: true, formula: true,
  },
  pastilles: {
    h1: "PASTILLES DENTIFRICE. LA RECHARGE.",
    bullets: ["Nano-hydroxyapatite concentrée", "1 pastille = 1 brossage exact", "62 brossages — un mois complet"],
    desc: "La pastille qui remplace le tube. Croquez, brossez : la nano-hydroxyapatite se dépose là où l'émail en a besoin et contribue à le reminéraliser. Sans fluor, sans SLS, une liste d'ingrédients que vous pouvez compter sur une main. C'est le produit par lequel on commence — et celui qu'on recommande.",
    science: true, formula: true, upgrade: true,
  },
  "bain-de-bouche": {
    h1: "BAIN DE BOUCHE. EN PASTILLES.",
    bullets: ["Haleine fraîche, sans flacon", "62 utilisations, sans alcool", "Croquez, rincez, souriez"],
    desc: "Le complément du brossage, au format CRUSH : une pastille, un peu d'eau, une bouche assainie. Sans alcool agressif, sans flacon d'un litre dans le placard.",
  },
  brosse: {
    h1: "LA BROSSE. EN BAMBOU.",
    bullets: ["Tête interchangeable : on change les poils, on garde le manche", "Poils souples, doux pour l'émail et les gencives", "Manche bambou qui ne gonfle pas"],
    desc: "Des poils souples pour respecter l'émail que vos pastilles nourrissent, un manche en bambou qui a sa place sur une belle étagère. Poils usés ? On change la tête, on garde le manche.",
  },
};

const Accordion = ({ variant }) => {
  const [open, setOpen] = useState(null);
  const list = variant === "Fraise" ? [...ingredients, fraiseIngredient] : ingredients;
  return (
    <div className="card" style={{ padding: 8 }} data-testid="formula-accordion">
      <div style={{ padding: 16 }}><span className="display" style={{ textTransform: "uppercase", letterSpacing: "0.03em", fontWeight: 700 }}>La formule, ingrédient par ingrédient.</span></div>
      {list.map((ing, i) => (
        <div key={i} style={{ borderTop: "1px solid var(--border)" }}>
          <button onClick={() => setOpen(open === i ? null : i)} data-testid={`ingredient-${i}`}
            style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", background: "none", border: "none", color: "var(--text)", padding: 16, cursor: "pointer", textAlign: "left" }}>
            <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {ing.image && (
                <img src={ing.image} alt="" loading="lazy"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                  style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", flexShrink: 0, border: "1px solid var(--border)" }} />
              )}
              <span><span style={{ fontWeight: 600 }}>{ing.name}</span> <span style={{ color: "var(--accent-light)" }}>— {ing.tag}</span></span>
            </span>
            <ChevronDown size={18} style={{ transform: open === i ? "rotate(180deg)" : "none", transition: "transform .2s", color: "var(--text-muted)" }} />
          </button>
          {open === i && <p style={{ margin: 0, padding: "0 16px 16px", color: "var(--text-muted)", fontSize: 14 }}>{ing.text}</p>}
        </div>
      ))}
    </div>
  );
};

export default function ProductPage({ id }) {
  const product = getProduct(id);
  const c = CONTENT[id];
  const { addItem } = useCart();
  const [variant, setVariant] = useState(product.variants && product.variants.length ? product.variants[0] : null);
  const [showSticky, setShowSticky] = useState(false);
  const buyRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      if (!buyRef.current) return;
      setShowSticky(buyRef.current.getBoundingClientRect().bottom < 0);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isKit = id === "kit";
  const [offerId, setOfferId] = useState("kit-decouverte");
  const selectedOffer = KIT_OFFERS.find((o) => o.id === offerId) || KIT_OFFERS[0];
  const buyPrice = isKit ? selectedOffer.price : product.price_eur;

  const add = () => {
    const cartId = isKit ? offerId : product.id;
    addItem(cartId, variant);
    toast.success(`${product.short_name} ajouté au panier`);
  };
  const soldOut = product.stock <= 0;

  // Upsell products
  const upsellIds = id === "kit" ? ["pastilles", "bain-de-bouche"] : ["kit", id === "pastilles" ? "bain-de-bouche" : "pastilles"];
  const upsells = upsellIds.map(getProduct).filter((p) => p && p.stock > 0);

  return (
    <main data-testid={`product-page-${id}`}>
      <section className="section" style={{ paddingBottom: 40 }}>
        <div className="wrap crush-g2" style={{ gap: 48 }} ref={buyRef}>
          <div>
            <Gallery images={GALLERIES[id] || [product.image]} alt={product.name} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <h1 className="title-xl" style={{ fontSize: "clamp(30px,6vw,48px)", margin: 0 }}>{c.h1}</h1>
            <Stars rating={product.rating} count={product.review_count} />
            <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
              <span className="price" style={{ fontSize: 32 }}>{product.price_eur} €</span>
              {product.anchor_price_eur && <span className="price-strike">{product.anchor_price_eur} €</span>}
            </div>
            {product.anchor_label && <span style={{ color: "var(--text-muted)", fontSize: 14, marginTop: -8 }}>{product.anchor_label}</span>}
            {isKit && <span style={{ color: "var(--accent-light)", fontSize: 14, fontWeight: 600 }} data-testid="kit-metal-box">Boîte de transport en métal offerte</span>}

            {product.variants && product.variants.length > 0 && (
              <div>
                <span className="eyebrow" style={{ color: "var(--text-muted)" }}>Parfum</span>
                <div style={{ display: "flex", gap: 12, marginTop: 8 }}>
                  {product.variants.map((v) => {
                    const active = variant === v;
                    const thumb = VARIANT_IMAGES[v];
                    return (
                      <button key={v} data-testid={`variant-${v}`} onClick={() => setVariant(v)}
                        className="btn" style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 16px 8px 8px", border: `2px solid ${active ? "var(--accent)" : "var(--border)"}`, color: active ? "var(--accent)" : "var(--text)", background: "transparent" }}>
                        {thumb && <img src={thumb} alt={v} style={{ width: 34, height: 34, borderRadius: 4, objectFit: "cover" }} />}
                        {v}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <ul style={{ listStyle: "none", padding: 0, margin: "8px 0", display: "flex", flexDirection: "column", gap: 10 }}>
              {c.bullets.map((b, i) => (
                <li key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", color: "var(--text-muted)" }}>
                  <Check size={18} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 2 }} />{b}
                </li>
              ))}
            </ul>

            {isKit && (
              <div style={{ marginTop: 4 }}>
                <span className="eyebrow" style={{ color: "var(--text-muted)", display: "block", marginBottom: 12 }}>Votre offre</span>
                <OfferSelector selectedId={offerId} onSelect={setOfferId} />
              </div>
            )}

            {soldOut ? (
              <button className="btn btn-disabled" disabled>Bientôt de retour</button>
            ) : (
              <button className="btn btn-primary" data-testid="add-to-cart" onClick={add}>Ajouter au panier — {buyPrice} €</button>
            )}
            <p style={{ color: "var(--text-muted)", fontSize: 13, margin: 0 }}>{config.shipping.delay_text} · Satisfait ou remboursé 30 jours · Paiement sécurisé</p>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="section" style={{ background: "var(--surface)", paddingTop: 48, paddingBottom: 48 }}>
        <div className="wrap"><Reveal><p style={{ fontSize: 18, maxWidth: "65ch", color: "var(--text)" }}>{c.desc}</p></Reveal>

          {id === "kit" && (
            <div style={{ marginTop: 40 }}>
              <h2 className="display" style={{ textTransform: "uppercase", letterSpacing: "0.03em", fontSize: 22 }}>Ce qu'il y a dans la boîte.</h2>
              <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 16 }}>
                {product.contents.map((line, i) => (
                  <div key={i} style={{ display: "flex", gap: 12, alignItems: "center", color: "var(--text-muted)" }}>
                    <span className="display" style={{ color: "var(--accent)", fontWeight: 700, fontSize: 18 }}>{`0${i + 1}`}</span>{line}
                  </div>
                ))}
              </div>
            </div>
          )}

          {c.upgrade && (
            <div className="card" style={{ padding: 24, marginTop: 32, border: "1px solid var(--accent-light)" }} data-testid="upgrade-box">
              <p style={{ margin: "0 0 14px" }}>Vous hésitez encore ? Pour 13 € de plus, le kit complet réunit dentifrice, bain de bouche, brosse et boîte.</p>
              <Link to="/kit" className="btn btn-secondary">Voir le kit — 45 €</Link>
            </div>
          )}
        </div>
      </section>

      {/* Science short */}
      {c.science && (
        <section className="section" style={{ paddingTop: 48, paddingBottom: 48 }}>
          <div className="wrap"><Reveal>
            <Badge>La science</Badge>
            <p style={{ maxWidth: "62ch", marginTop: 16, color: "var(--text-muted)" }}>La nano-hydroxyapatite est le minéral qui compose 97 % de votre émail. Appliquée au brossage, elle se dépose dans les microfissures et contribue à le reminéraliser — l'alternative au fluor la plus documentée au monde.</p>
            <Link to="/science" className="link-accent" style={{ display: "inline-block", marginTop: 12 }}>Voir toutes les études →</Link>
          </Reveal></div>
        </section>
      )}

      {/* Formula accordion */}
      {c.formula && (
        <section className="section" style={{ background: "var(--surface)", paddingTop: 48, paddingBottom: 48 }}>
          <div className="wrap"><Reveal><Accordion variant={variant} /></Reveal></div>
        </section>
      )}

      {/* Upsell */}
      <section className="section" style={{ paddingTop: 48, paddingBottom: 48 }}>
        <div className="wrap">
          <h2 className="title-xl" style={{ fontSize: 28 }}>COMPLÉTEZ VOTRE ROUTINE.</h2>
          <div style={{ display: "grid", gap: 20, gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", marginTop: 28 }}>
            {upsells.map((p, i) => (<ProductCard key={p.id} product={p} wide={id !== "kit" && i === 0} badge={id !== "kit" && i === 0 ? "Le produit phare" : null} />))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="section" style={{ background: "var(--surface)", paddingTop: 48, paddingBottom: 48 }}>
        <div className="wrap">
          <h2 className="title-xl" style={{ fontSize: 28 }}>ILS EN PARLENT.</h2>
          <div style={{ display: "grid", gap: 20, gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", marginTop: 28 }}>
            {(reviews[id] || []).map((r, i) => (
              <ReviewCard key={i} review={r} testid={`review-${id}-${i}`} />
            ))}
          </div>
        </div>
      </section>

      {/* Sticky mobile bar */}
      {showSticky && !soldOut && (
        <div data-testid="sticky-bar" style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 60, background: "var(--surface)", borderTop: "1px solid var(--border)", padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 600 }}>{product.short_name}</div><span className="price">{buyPrice} €</span></div>
          <button className="btn btn-primary" style={{ padding: "12px 20px", minHeight: 44 }} data-testid="sticky-add" onClick={add}>Ajouter</button>
        </div>
      )}
    </main>
  );
}
